import { ArtworkImage } from './ArtworkImage';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { GameState, UserAction } from '../api';
import { findInvestigator } from '../utils/investigators';
import { ActionsList } from './ActionsList';
import { InvestigatorMarker } from './InvestigatorMarker';

type Props = { state: GameState; actions: UserAction[]; loading: boolean; language?: string; onExecuteAction: (id: string) => void;
  requestedActionId?: string; onRequestHandled?: () => void };
type Group = { key: string; actions: UserAction[] };
const eligible = (group: Group) => group.actions.filter(action => !action.disabled);

function groupChoices(actions: UserAction[]): Group[] {
  const groups = new Map<string, UserAction[]>();
  for (const action of actions) {
    const trade = action.trade!;
    const key = JSON.stringify([trade.direction, trade.resource,
      trade.direction === 'take' ? trade.counterpartInvestigatorName : null, trade.itemKey]);
    const existing = groups.get(key);
    if (existing) existing.push(action);
    else groups.set(key, [action]);
  }
  return Array.from(groups, ([key, choices]) => ({ key, actions: choices }));
}

export function TradeActions({ state, actions, loading, language, onExecuteAction, requestedActionId, onRequestHandled }: Props) {
  const groups = useMemo(() => groupChoices(actions.filter(action => action.trade && action.trade.direction !== 'finish' && !action.disabled)), [actions]);
  const [selection, setSelection] = useState<{ key: string; amount: string; recipient?: string; choosingRecipient?: boolean } | null>(null);
  const chooser = useRef<HTMLDivElement>(null);
  const opener = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (selection?.key) chooser.current?.querySelector<HTMLElement>('input, button')?.focus();
  }, [selection?.key]);
  // Keep local choices only while the registered IDs that justified them are current.
  const signature = JSON.stringify(actions.map(action => [action.id, action.investigatorName, action.disabled, action.trade, action.card]));
  const [previousSignature, setPreviousSignature] = useState(signature);
  if (previousSignature !== signature) {
    setPreviousSignature(signature);
    setSelection(null);
  }
  const selected = groups.find(group => group.key === selection?.key);
  const finish = actions.find(action => action.trade?.direction === 'finish');
  const extras = actions.filter(action => !action.trade);
  const execute = (action: UserAction) => {
    if (loading || action.disabled) return;
    setSelection(null);
    onExecuteAction(action.id);
  };
  useEffect(() => {
    if (!requestedActionId) return;
    const group = groups.find(candidate => candidate.actions.some(action => action.id === requestedActionId));
    if (group && !loading) {
      opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      setSelection({ key: group.key, amount: String(Math.min(...eligible(group).map(action => action.trade!.amount ?? 1))) });
    }
    onRequestHandled?.();
  }, [requestedActionId, loading, onRequestHandled, groups]);
  const peers = Array.from(new Set(groups.flatMap(group => group.actions.map(action => action.trade!.counterpartInvestigatorName!))));
  const marker = (name: string) => <InvestigatorMarker investigator={findInvestigator(state, name) ?? { name, delayed: false, isDevoured: false }} size={32} offsetByIndex={false} />;
  const open = (group: Group) => {
    if (loading || !eligible(group).length) return;
    opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (group.actions[0].trade!.resource === 'item' && eligible(group).length === 1) execute(eligible(group)[0]);
    else setSelection({ key: group.key, amount: String(Math.min(...eligible(group).map(action => action.trade!.amount ?? 1))) });
  };
  const renderGroups = (choices: Group[]) => {
    const items = choices.filter(group => group.actions[0].trade!.resource === 'item');
    const resources = choices.filter(group => group.actions[0].trade!.resource !== 'item');
    return <>
      <ActionsList compact actions={items.map(group => group.actions[0])}
        getActionLabel={action => action.trade!.direction === 'give' ? `Give ${(action.card as { name?: string })?.name ?? 'card'}` : action.description || action.name}
        loading={loading} language={language} onExecuteAction={id => { const group = items.find(item => item.actions[0].id === id); if (group) open(group); }} />
      <div className="trade-resources">{resources.map(group => {
        const trade = group.actions[0].trade!;
        const label = `${trade.direction === 'give' ? 'Give' : 'Take'} ${trade.resource === 'money' ? 'money' : 'clues'}`;
        return <button type="button" className="trade-resource" key={group.key} aria-label={label}
          disabled={loading || !eligible(group).length} onClick={() => open(group)}>
          <ArtworkImage src={trade.resource === 'money' ? '/money.png' : '/Clue.png'} alt="" />{label}
        </button>;
      })}</div>
    </>;
  };
  const amount = Number(selection?.amount);
  const amountChoices = selected ? eligible(selected).filter(action => action.trade!.resource === 'item' || action.trade!.amount === amount) : [];
  const recipients = Array.from(new Set(amountChoices.map(action => action.trade!.counterpartInvestigatorName!)));
  const recipient = recipients.length === 1 ? recipients[0] : selection?.recipient;
  const chosen = amountChoices.find(action => action.trade!.counterpartInvestigatorName === recipient);
  const itemChoice = selected?.actions[0].trade!.resource === 'item';
  const give = selected?.actions[0].trade!.direction === 'give';
  return <section className="trade-actions" aria-label="Trade">
    <h2>Trade</h2>
    {selected && selection && <div ref={chooser} className="trade-chooser" role="group" aria-label="Choose transfer">
      <h3>{give ? 'Give' : 'Take'} {selected.actions[0].trade!.resource === 'item' ? (selected.actions[0].card as { name?: string })?.name : selected.actions[0].trade!.resource === 'money' ? 'money' : 'clues'}</h3>
      {!itemChoice && <label>Amount<input type="number" min={1} step={1}
        max={Math.max(...eligible(selected).map(action => action.trade!.amount ?? 0))} value={selection.amount} disabled={loading}
        onChange={event => setSelection({ key: selection.key, amount: event.target.value })} /></label>}
      {(itemChoice || selection.choosingRecipient) && <div className="trade-recipients" role="group" aria-label="Choose investigator">
        {recipients.map(name => <button type="button" key={name} disabled={loading} aria-pressed={recipient === name}
          onClick={() => {
            if (itemChoice) { const action = amountChoices.find(choice => choice.trade!.counterpartInvestigatorName === name); if (action) execute(action); }
            else setSelection({ ...selection, recipient: name });
          }}>{marker(name)}{name}</button>)}
      </div>}
      {!itemChoice && recipient && <p>{give ? 'To' : 'From'}: {recipient}</p>}
      <div className="trade-chooser-controls">
        {!itemChoice && (recipients.length > 1 && !selection.choosingRecipient
          ? <button type="button" disabled={loading || !Number.isInteger(amount) || !amountChoices.length} onClick={() => setSelection({ ...selection, choosingRecipient: true })}>Continue</button>
          : <button type="button" disabled={loading || !Number.isInteger(amount) || !chosen} onClick={() => { if (chosen) execute(chosen); }}>
            {give ? 'Give' : 'Take'} {selected.actions[0].trade!.resource === 'money' ? `$${selection.amount}` : `${selection.amount} clue${amount === 1 ? '' : 's'}`}
          </button>)}
        {selection.choosingRecipient && <button type="button" disabled={loading} onClick={() => setSelection({ ...selection, choosingRecipient: false, recipient: undefined })}>Back</button>}
        <button type="button" disabled={loading} onClick={() => { setSelection(null); opener.current?.focus(); }}>Cancel</button>
      </div>
      {!itemChoice && !amountChoices.length && <p role="status">Choose an available whole-number amount.</p>}
    </div>}
    <section className="trade-section" aria-label="Your tradeable holdings"><h3>Your holdings</h3>
      {renderGroups(groups.filter(group => group.actions[0].trade!.direction === 'give'))}
    </section>
    {peers.map(name => {
      const incoming = groups.filter(group => group.actions[0].trade!.direction === 'take' && group.actions[0].trade!.counterpartInvestigatorName === name);
      return <section key={name} className="trade-section" aria-label={`${name} tradeable holdings`}><h3>{marker(name)}{name}</h3>{renderGroups(incoming)}</section>;
    })}
    <ActionsList compact actions={extras} loading={loading} language={language} onExecuteAction={onExecuteAction} />
    {finish && <button type="button" className="trade-finish" disabled={loading || !!finish.disabled} onClick={() => execute(finish)}>{finish.name}</button>}
  </section>;
}
